/**
 * A low-reading card's one read: the client's recent readings of the kind the task is about
 * (`GET /vitals/customers/{id}/readings`, attention-queue R27), projected by `model/vitalReadings`.
 *
 * Gated on the card being on screen, like every lazy read on these cards (`useSessionReadout` says
 * why it is geometry rather than the row's index), so a deck of low readings costs the read of the
 * one the trainer opened.
 */

import { useGetVitalsCustomersCustomerIdReadings } from '~/shared/api/generated/endpoints/vitals/vitals';

import type { VitalKind } from '../model/taskContent';
import { vitalReadings, type VitalReadings } from '../model/vitalReadings';

/** The catalogue's name for each reading (`GET /vitals/catalog`) — what the read is asked for. */
const VITAL_TYPE: Readonly<Record<VitalKind, string>> = {
  sleep: 'SleepDuration',
  energy: 'EnergyLevel',
};

export interface VitalReadingsState {
  readonly view: VitalReadings;
  /** The read is on its way. `false` before the card has been on screen: nothing was asked yet. */
  readonly pending: boolean;
  readonly failed: boolean;
}

export function useVitalReadings(customerId: number | null, kind: VitalKind, onScreen: boolean): VitalReadingsState {
  const wanted = customerId !== null && onScreen;
  const read = useGetVitalsCustomersCustomerIdReadings(
    // Guarded by `enabled`: an item with no client never reaches the network.
    customerId ?? 0,
    { type: VITAL_TYPE[kind] },
    { query: { enabled: wanted } },
  );
  return { view: vitalReadings(read.data), pending: wanted && read.isPending, failed: wanted && read.isError };
}
